import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { displayDate, formatDate, formatPhoneNumber, formatToPKR, getInitials } from "@/lib/helper";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CalendarIcon, Check, CheckCircle, CheckSquare, Coins, Mail, Phone, X, Clock, Users, CreditCard } from "lucide-react";
import { Card, CardHeader } from "@/components/ui/card";
import { useGetMemberByIdQuery, useGetAllMemberQuery } from "@/services/memberApi";
import { useUpdateBookingStatusMutation, useSwapMemberMutation } from "@/services/facilityApis";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ErrorType } from "@/types";
import warning from "@/assets/warning.svg";
import SwapBooker from "./swap-booker";
import { FacilityBooking } from "@/types/facility/booking";
const { VITE_VIEW_S3_URL } = import.meta.env;

interface BookingRequestDetails {
  isOpen: boolean;
  onClose: () => void;
  data: FacilityBooking | null;
  refetch: () => void;
}

const BookingRequestDetails = ({
  isOpen,
  onClose,
  data,
  refetch,
}: BookingRequestDetails) => {
  const { toast } = useToast();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  // Add state for dialogs
  const [isApproveDialogOpen, setIsApproveDialogOpen] = React.useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState("");
  const [isProcessingApproval, setIsProcessingApproval] = React.useState(false);
  const [isSwapBookerOpen, setIsSwapBookerOpen] = React.useState(false);

  // API mutations
  const [updateBookingStatus, { isLoading: isUpdating }] = useUpdateBookingStatusMutation();
  const [swapMember, { isLoading: isSwapping }] = useSwapMemberMutation();

  // Add key prop to force re-query when member_id changes
  const memberId = data?.member_id || 0;

  const { data: memberInfo, isLoading: memberInfoLoading, refetch: refetchMember } =
    useGetMemberByIdQuery(
      { org_id: orgId, id: Number(memberId) },
      {
        skip: !memberId || isOpen === false,
        // Force refetch when member_id changes
        refetchOnMountOrArgChange: true,
      }
    );

  const { data: allMembers, isLoading: isMembersLoading } = useGetAllMemberQuery(
    { org_id: orgId, query: "" },
    { skip: !isSwapBookerOpen }
  );

  // Handle approve booking
  const handleApprove = async () => {
    if (!data) return;

    try {
      setIsProcessingApproval(true);

      const response = await updateBookingStatus({
        booking_id: data.id as number,
        action: "booking_approved",
        org_id: orgId,
      }).unwrap();

      console.log("approve", { response });
      toast({
        variant: "success",
        title: response.detail,
      });

      refetch();
      setIsApproveDialogOpen(false);
      onClose();
    } catch (error) {
      console.error("Error approving booking", error);
      handleApiError(error);
    } finally {
      setIsProcessingApproval(false);
    }
  };

  // Handle reject booking
  const handleReject = async () => {
    if (!data) return;

    if (!rejectionReason.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please provide a reason for rejection.",
      });
      return;
    }

    try {
      const response = await updateBookingStatus({
        booking_id: data.id as number,
        action: "booking_rejected",
        reason: rejectionReason,
        org_id: orgId,
      }).unwrap();
      
      console.log("reject", { response });

      toast({
        variant: "success",
        title: response.detail,
      });

      refetch();
      setIsRejectDialogOpen(false);
      setRejectionReason("");
      onClose();
    } catch (error) {
      console.error("Error rejecting booking", error);
      handleApiError(error);
    }
  };

  // Error handler
  const handleApiError = (error: any) => {
    if (error && typeof error === "object" && "data" in error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError.data?.detail || typedError.data?.message || "Something went wrong."}`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Something went wrong. Please try again.",
      });
    }
  };

  // Handle text input to ensure only single spaces
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Replace multiple spaces with a single space
    const formattedText = value.replace(/\s+/g, " ");

    // Apply character limit (150 chars)
    if (formattedText.length <= 150) {
      setRejectionReason(formattedText);
    }
  };

  // Swap booker handler
  const handleSwapBooker = async (newBookerId: number | string) => {
    if (!data) return;

    try {
      await swapMember({
        booking_id: data.id as number,
        target_member_id: Number(newBookerId),
        org_id: orgId,
      }).unwrap();

      toast({
        title: "Success",
        description: "Booker has been swapped successfully.",
      });

      refetch();
      onClose();
    } catch (error) {
      console.error("Error swapping booker:", error);
      handleApiError(error);
      throw error; // Rethrow to let the SwapBooker component handle the error
    }
  };

  if (!data) return null;

  // Get status badge styling
  const getStatusBadge = (status: string) => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      cancelled: "bg-gray-100 text-gray-800",
    };

    const color = statusColors[status?.toLowerCase() as keyof typeof statusColors] || "bg-gray-100 text-gray-800";

    return {
      label: status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase() || status,
      bgColor: color,
    };
  };

  const statusBadge = getStatusBadge(data.status as string);
  const paymentStatusBadge = getStatusBadge(data.payment_status as string);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent
          hideCloseButton
          className="bg-formbg w-full lg:max-w-4xl p-0 custom-scrollbar h-full"
        >
          <SheetHeader className="flex justify-between border-b items-center sticky top-0 z-40 bg-white shadow-sm">
            <div className="flex justify-between w-full p-4">
              <div className="flex items-center gap-2">
                <SheetClose asChild>
                  <Button size="icon" variant="ghost">
                    <X className="h-4 w-4" />
                  </Button>
                </SheetClose>
                <SheetTitle className="text-xl">
                  Booking Details
                </SheetTitle>
              </div>
              {/* Action buttons for pending requests */}
              {data.status?.toLowerCase() === "pending" && (
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsRejectDialogOpen(true)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => setIsApproveDialogOpen(true)}
                    className="bg-primary text-white"
                  >
                    Approve
                  </Button>
                </div>
              )}
            </div>
          </SheetHeader>

          <div className="p-6">
            <div className="space-y-6">
              <Card className="w-full px-3 bg-white rounded-lg overflow-hidden">
                {/* Facility info section */}
                <CardHeader className="p-3 border-b mb-3">
                  <div className="flex items-center justify-between">
                    <div>Facility Details</div>
                    <div className="text-sm font-medium">
                      {formatToPKR(data?.total || 0)}
                    </div>
                  </div>
                </CardHeader>
                <div className="flex items-start gap-4 pb-4">
                  {/* <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500 overflow-hidden">   
                    {data.image_url ? (
                      <img
                        src={`${VITE_VIEW_S3_URL}/${data.image_url[0]}`}
                        alt={data.facility_name || "Facility"}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <span>{data.facility_name?.charAt(0).toUpperCase() || "F"}</span>
                    )}
                  </div> */}

                  <div className="flex-1">
                    <div className="flex flex-row items-center justify-between">
                      <div className="flex flex-row items-center gap-2">
                        <h3 className="font-medium capitalize">
                          {data?.facility_name}
                        </h3>
                        <Badge className={`${statusBadge.bgColor}`}>
                          {statusBadge.label}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex flex-row justify-between items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        <Users className="h-4 w-4" />
                        Max Attendees: {(data as FacilityBooking)?.max_attendees || "N/A"}
                      </span>
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        <CreditCard className="h-4 w-4" />
                        Price of Booking: {formatToPKR(data?.price || 0)}
                      </span>
                    </div>
                  </div>
                </div>

              </Card>

              {/* Booking Details Card */}
              <Card className="w-full px-3 bg-white rounded-lg overflow-hidden">
                <CardHeader className="p-3 border-b mb-3">
                  <div>Booking Details</div>
                </CardHeader>
                <div className="flex items-start gap-4 pb-4">
                  <div className="flex-1">
                    <div className="flex flex-row justify-between items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        Booked on: {displayDate(data?.created_at)}
                      </span>
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        Booked for: {displayDate(data?.booking_date)}
                      </span>
                    </div>
                    <div className="flex flex-row justify-between items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Slot: {data?.start_time} - {data?.end_time}
                      </span>
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        <span className="font-medium">Payment Status:</span>
                        <Badge className={`${paymentStatusBadge.bgColor} ml-2`}>
                          {paymentStatusBadge.label}
                        </Badge>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Add-ons section */}
                {data.add_ons && data.add_ons.length > 0 && (
                  <>
                    <CardHeader className="p-3 border-b mb-3">
                      <div className="flex items-center justify-between">
                        <div>Add-Ons</div>
                      </div>
                    </CardHeader>
                    <div className="grid grid-cols-2 gap-4 pb-4">
                      {data.add_ons.map((addon: any, index: number) => (
                        <div key={addon.id || index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                          <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500 overflow-hidden">
                            {addon.image_url ? (
                              <img
                                src={addon.image_url.includes('https://')
                                  ? addon.image_url
                                  : `${VITE_VIEW_S3_URL}/${addon.image_url}`}
                                alt={addon.name || "Add-on"}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <span className="text-lg font-semibold">
                                {addon.name?.charAt(0).toUpperCase() || "A"}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{addon.name || "Add-on"}</p>
                            <p className="text-xs text-gray-500 capitalize">Type: {addon.type}</p>
                            <p className="text-xs text-gray-500">Price: {formatToPKR(addon.price || 0)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card>

              {/* Member Details Card */}
              <Card className="w-full px-3 bg-white rounded-lg overflow-hidden">
                {/* Member info section */}
                <CardHeader className="p-3 border-b mb-3">
                  <div className="flex items-center justify-between">
                    <div>Member Details</div>
                    <div className="text-sm">
                      <Button
                        variant="outline"
                        className="border-primary text-black hover:bg-primary hover:text-white"
                        onClick={() => setIsSwapBookerOpen(true)}
                      >
                        Swap Booker
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <div className="flex items-start gap-4 pb-4">
                  <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500 overflow-hidden">
                    {memberInfo?.profile_img ? (
                      <img
                        src={memberInfo?.profile_img !== "" && memberInfo?.profile_img
                          ? memberInfo?.profile_img.includes(
                            VITE_VIEW_S3_URL
                          )
                            ? memberInfo?.profile_img
                            : `${VITE_VIEW_S3_URL}/${memberInfo?.profile_img}`
                          : ""}
                        alt={memberInfo?.first_name + " " + memberInfo?.last_name || "User"}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (

                      <span>{getInitials(memberInfo?.first_name + " " + memberInfo?.last_name)}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-row items-center justify-between">
                      <div>
                        <h3 className="font-semibold capitalize">
                          {memberInfo?.first_name + " " + memberInfo?.last_name || "Member Name"}
                        </h3>
                      </div>
                    </div>

                    <div>
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        <h3 className="font-medium capitalize">
                          Member ID:
                        </h3>
                        {memberInfo?.own_member_id || "N/A"}
                      </span>
                      <div className="flex flex-row items-center gap-2 mt-1">
                        <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                          <Mail className="h-4 w-4" />
                          {memberInfo?.email || "N/A"}
                        </span>
                        <span className="text-sm text-gray-500"> | </span>
                        <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                          <Phone className="h-4 w-4" />
                          {formatPhoneNumber(memberInfo?.mobile_number || "N/A")}
                        </span>
                        <span className="text-sm text-gray-500"> | </span>
                        <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                          <span className="font-medium">Membership Plan:</span>
                          {(memberInfo as any)?.membership_plan?.name || "N/A"}
                        </span>
                        {(memberInfo as any)?.membership_plan?.status && (
                          <Badge className={`${getStatusBadge((memberInfo as any)?.membership_plan?.status).bgColor} ml-2`}>
                            {getStatusBadge((memberInfo as any)?.membership_plan?.status).label}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Rejection reason if exists */}
              {data?.reason && (
                <div className="space-y-2">
                  <h3 className="font-medium text-base">Rejection Reason</h3>
                  <p className="text-sm p-3 bg-red-50 border border-red-100 rounded-md break-all">
                    {data?.reason}
                  </p>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Approve Confirmation Dialog */}
      <Dialog open={isApproveDialogOpen} onOpenChange={setIsApproveDialogOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
            <DialogDescription>
              <div className="flex flex-col items-center justify-center gap-4 mt-4">
                <Check className="h-16 w-16 text-green-500" />
                <p className="text-center">
                  Are you sure you want to approve this facility booking?
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setIsApproveDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={isProcessingApproval}
            >
              {isProcessingApproval ? "Processing..." : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <AlertDialog
        open={isRejectDialogOpen}
        onOpenChange={(open) => {
          // Only allow closing with the cancel button, not when clicking outside
          if (open === false && rejectionReason.trim() === "") {
            return;
          }
          setIsRejectDialogOpen(open);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogDescription>
              <div className="flex flex-col items-center justify-center gap-4">
                <img src={warning} alt="warning" className="w-18 h-18" />
                <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                  Reject this facility booking?
                </AlertDialogTitle>
              </div>
              <div className="my-4">
                <Label htmlFor="reason" className="text-sm font-medium">
                  Rejection Reason
                </Label>
                <Textarea
                  id="reason"
                  className="mt-2"
                  placeholder="Please provide a reason for rejection"
                  value={rejectionReason}
                  onChange={handleTextChange}
                  rows={4}
                  maxLength={150}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {rejectionReason.length}/150 characters
                </div>
              </div>
              <div className="w-full flex justify-between items-center gap-3 mt-4">
                <AlertDialogCancel
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRejectDialogOpen(false);
                    setRejectionReason("");
                  }}
                  disabled={isUpdating}
                  className="w-full border border-primary font-semibold"
                >
                  <i className="fa fa-xmark text-base px-1"></i>
                  Cancel
                </AlertDialogCancel>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!rejectionReason.trim()) {
                      toast({
                        variant: "destructive",
                        title: "Error",
                        description: "Please provide a reason for rejection.",
                      });
                      return;
                    }
                    handleReject();
                  }}
                  disabled={isUpdating}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  {isUpdating ? "Rejecting..." : "Reject"}
                </Button>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>

      <SwapBooker
        isOpen={isSwapBookerOpen}
        onClose={async () => {
          await Promise.all([
            refetch(),
            refetchMember()])
          setIsSwapBookerOpen(false)
        }}
        currentBooker={{
          id: data?.member_id || 0,
          name: memberInfo?.first_name + " " + memberInfo?.last_name || "Unknown",
        }}
        availableBookers={
          allMembers?.data
            ? allMembers.data
              .filter((member) => member && member.id && member.id !== data?.member_id)
              .map((member) => ({
                id: member.id,
                name: `${member.first_name || ''} ${member.last_name || ''}`.trim(),
              }))
            : []
        }
        onSwap={handleSwapBooker}
        facilityId={data.facility_id as number}
      />
    </>
  );
};

export default BookingRequestDetails;